'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { useDotsRange } from './hooks/use-dots-range';
import { PhotoCarouselDot } from './photo-carousel-dot';
import { photoCarouselDotsStyles } from './styles/photo-carousel-dots.styles';
import type { PhotoCarouselDotsPropsInterface } from './types/photo-carousel-dots.types';

/**
 * An unordered list of `PhotoCarousel.Dot` components [CDS-5859].
 *
 * The dots are decorative — they show progress through the slides and are hidden
 * from assistive technologies, which read the slide's own label instead [CDS-5858,
 * CDS-5859]. Keyboard users navigate with `PhotoCarousel.PreviousButton` and
 * `PhotoCarousel.NextButton`.
 *
 * @since 8.66.0
 */
export const PhotoCarouselDots = forwardRef<HTMLUListElement, PhotoCarouselDotsPropsInterface>(
  (props: PhotoCarouselDotsPropsInterface, forwardedRef) => {
    const { activeIndex: activeIndexContext, totalSlides: totalSlidesContext } =
      useContext(PhotoCarouselRootContext);

    const {
      activeIndex = activeIndexContext || 0,
      'aria-label': _ariaLabel,
      className,
      css: cssProp,
      maxDotsToDisplay = 5,
      totalSlides = totalSlidesContext || 0,
      renderDot = ({ key, dotProps }) => <PhotoCarouselDot key={key} {...dotProps} />,
      ...rest
    } = props;

    const dotsToRender = useDotsRange({
      activeIndex,
      totalSlides,
      maxDotsToDisplay,
    });

    const content = dotsToRender.map(({ key, order, isActive, isContinuation, isHidden }) =>
      renderDot({
        // `order` is an inline style rather than a Panda class because
        // `maxDotsToDisplay` is consumer-controlled and so unbounded.
        dotProps: { isActive, isContinuation, isHidden, style: { order } },
        key,
      }),
    );

    const styles = css(photoCarouselDotsStyles, cssProp);

    /*
     * Only show dots if there are 2 or more slides.
     * Placed after the hooks above so they're always called.
     */
    if (totalSlides < 2) {
      return null;
    }

    return (
      <ul
        aria-hidden
        ref={forwardedRef}
        data-c11n-component="PhotoCarousel.Dots"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_photo-carousel__dots', styles, className)}
        {...rest}
      >
        {content}
      </ul>
    );
  },
);

PhotoCarouselDots.displayName = 'PhotoCarousel.Dots';
